"use client";

import Link from "next/link";
import Image from "next/image";
import type { MintingState } from "./hooks";

// ============================================================================
// Types
// ============================================================================

export interface MintingSectionProps {
  /** Current minting state from useMintingMachine */
  mintingState: MintingState;
  /** Callback when user clicks mint button */
  onMint: () => void;
  /** Callback when user clicks retry button */
  onRetry: () => void;
  /** Optional image URL for the asset preview */
  assetImage?: string;
  /** Optional video URL for the asset preview (takes priority over image) */
  assetVideo?: string;
  /** Title for asset (used in alt text) */
  assetTitle: string;
  /** Primary color for whitelabel styling */
  primaryColor?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

export interface AssetCardProps {
  message: string;
  assetImage?: string;
  assetVideo?: string;
  assetTitle: string;
}

export function AssetCard({ message, assetImage, assetVideo, assetTitle }: AssetCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      <div className="p-6">
        <div className="text-center">
          <div className="mx-auto w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center mb-4 overflow-hidden relative">
            {assetVideo ? (
              <video
                src={assetVideo}
                className="w-full h-full object-contain"
                muted
                loop
                playsInline
                autoPlay
                crossOrigin="anonymous"
              />
            ) : assetImage ? (
              <Image
                src={assetImage}
                alt={`${assetTitle} Digital Asset`}
                className="object-contain"
                fill
                unoptimized
              />
            ) : (
              <div className="text-4xl">&#127873;</div>
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {assetTitle || 'Exclusive Digital Asset'}
          </h3>
          <p className="text-gray-600 mb-4">{message}</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Status Components
// ============================================================================

function SuccessStatus({ mintingState }: { mintingState: MintingState }) {
  return (
    <div className="bg-green-50 rounded-lg border-l-4 border-green-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-green-800 mb-2">Successfully Claimed!</h3>
        <p className="text-green-700 mb-4">
          Your NFT has been minted to your WAX wallet.
        </p>
        {mintingState.mintedAssetId && (
          <p className="text-sm text-green-600 mb-2">
            Asset ID: {mintingState.mintedAssetId}
          </p>
        )}
        <Link
          href="/my-assets"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          View my Digital Asset
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function FailedStatus({
  mintingState,
  onRetry
}: {
  mintingState: MintingState;
  onRetry: () => void;
}) {
  return (
    <div className="bg-red-50 rounded-lg border-l-4 border-red-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-red-800 mb-2">Minting Failed</h3>
        <p className="text-red-700 mb-4">
          {mintingState.errorMessage || 'An error occurred during minting.'}
        </p>
        {mintingState.retryCount !== undefined && mintingState.retryCount < 3 && (
          <p className="text-sm text-red-600 mb-4">
            Retry attempts: {mintingState.retryCount}/3
          </p>
        )}
        {mintingState.canRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Retry Minting
          </button>
        )}
      </div>
    </div>
  );
}

function WalletRequiredStatus({ mintingState }: { mintingState: MintingState }) {
  return (
    <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-yellow-800 mb-2">WAX Wallet Required</h3>
        <p className="text-yellow-700 mb-4">
          {mintingState.errorMessage || 'Please connect your WAX wallet to claim this digital asset.'}
        </p>
        <Link
          href="/dashboard/settings"
          className="inline-flex items-center px-6 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors"
        >
          Connect WAX Wallet
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

interface ProcessingStatusProps {
  mintingState: MintingState;
  assetImage?: string;
  assetVideo?: string;
  assetTitle: string;
}

function ProcessingStatus({ mintingState, assetImage, assetVideo, assetTitle }: ProcessingStatusProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="text-center">
        <AssetCard
          message="Minting your NFT..."
          assetImage={assetImage}
          assetVideo={assetVideo}
          assetTitle={assetTitle}
        />
        <div className="mt-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {mintingState.status === 'claiming' ? 'Initiating Claim...' : 'Minting Your NFT...'}
          </h3>
          <p className="text-gray-600">
            {mintingState.status === 'polling'
              ? 'This is taking longer than expected. Please wait...'
              : 'Please wait while we mint your exclusive digital asset to the blockchain.'}
          </p>
          {mintingState.status === 'polling' && mintingState.errorMessage && (
            <p className="text-yellow-600 mt-2 text-sm">{mintingState.errorMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface ReadyToMintStatusProps {
  onMint: () => void;
  assetImage?: string;
  assetVideo?: string;
  assetTitle: string;
  primaryColor?: string;
}

function ReadyToMintStatus({ onMint, assetImage, assetVideo, assetTitle, primaryColor }: ReadyToMintStatusProps) {
  // Button styling - use primary color if provided, otherwise default gradient
  const buttonStyle = primaryColor
    ? { backgroundColor: primaryColor }
    : undefined;
  const buttonClassName = primaryColor
    ? "px-8 py-4 text-white rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg hover:opacity-90"
    : "px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg";

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="text-center">
        <AssetCard
          message="You've successfully verified your location!"
          assetImage={assetImage}
          assetVideo={assetVideo}
          assetTitle={assetTitle}
        />
        <div className="mt-6">
          <button
            onClick={onMint}
            className={buttonClassName}
            style={buttonStyle}
          >
            Mint Digital Asset
          </button>
          <p className="mt-3 text-sm text-gray-500">
            Click to mint your exclusive NFT to your WAX account
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * MintingSection renders the appropriate UI based on the minting state.
 *
 * States handled:
 * - success: Shows success message with transaction link
 * - failed: Shows error message with retry button
 * - requires_wallet: Shows wallet connection prompt
 * - claiming/minting/polling: Shows processing animation
 * - idle: Shows mint button (ready state)
 */
export function MintingSection({
  mintingState,
  onMint,
  onRetry,
  assetImage,
  assetVideo,
  assetTitle,
  primaryColor,
}: MintingSectionProps) {
  // Success state
  if (mintingState.status === 'success') {
    return <SuccessStatus mintingState={mintingState} />;
  }

  // Failed state
  if (mintingState.status === 'failed') {
    return <FailedStatus mintingState={mintingState} onRetry={onRetry} />;
  }

  // Requires wallet state
  if (mintingState.status === 'requires_wallet') {
    return <WalletRequiredStatus mintingState={mintingState} />;
  }

  // Processing states (claiming, minting, polling)
  if (mintingState.status === 'claiming' || mintingState.status === 'minting' || mintingState.status === 'polling') {
    return (
      <ProcessingStatus
        mintingState={mintingState}
        assetImage={assetImage}
        assetVideo={assetVideo}
        assetTitle={assetTitle}
      />
    );
  }

  // Ready to mint (idle state)
  return (
    <ReadyToMintStatus
      onMint={onMint}
      assetImage={assetImage}
      assetVideo={assetVideo}
      assetTitle={assetTitle}
      primaryColor={primaryColor}
    />
  );
}
